import * as fs from "node:fs";
import * as path from "node:path";

import { CliError, EXIT_INPUT, EXIT_OK, EXIT_USAGE, type CommandOutput } from "../common.js";
import { packageRoot, packageVersion } from "../package-root.js";

interface ChangelogOptions {
  _args?: string[];
  from?: unknown;
  to?: unknown;
  unreleased?: unknown;
}

interface PackageJson {
  version?: string;
}

const VERSION_RE = /^\d+\.\d+\.\d+$/;
const UNRELEASED = "unreleased";

function changesDir(): string {
  return path.join(packageRoot(), "changes");
}

function readPackageVersion(): string {
  return packageVersion();
}

function versionParts(version: string): [number, number, number] {
  if (!VERSION_RE.test(version)) {
    throw new CliError("Version must use x.y.z.", {
            exitCode: EXIT_USAGE,
            errorCode: "CHANGELOG_VERSION_INVALID",
            hint: "Pass a released version such as `scriptctl changelog 0.25.10`.",
            data: { expected: ["x.y.z"], actual: [version || "<empty>"] },
          });
  }
  const [major, minor, patch] = version.split(".").map((part) => Number(part)) as [number, number, number];
  return [major, minor, patch];
}

function compareVersions(a: string, b: string): number {
  const av = versionParts(a);
  const bv = versionParts(b);
  for (let i = 0; i < 3; i++) {
    if (av[i] !== bv[i]) return av[i]! - bv[i]!;
  }
  return 0;
}

function listReleasedVersions(): string[] {
  const dir = changesDir();
  return fs.readdirSync(dir)
    .filter((name) => name.endsWith(".md"))
    .map((name) => name.slice(0, -".md".length))
    .filter((version) => VERSION_RE.test(version))
    .sort(compareVersions);
}

function readChangeFile(version: string): string {
  return fs.readFileSync(path.join(changesDir(), `${version}.md`), "utf-8").trimEnd();
}

function missingVersion(version: string, available: string[]): CliError {
  return new CliError(`No changelog entry exists for ${version}.`, {
           exitCode: EXIT_INPUT,
           errorCode: "CHANGELOG_VERSION_NOT_FOUND",
           hint: available.length > 0
        ? `Available versions: ${available.join(", ")}`
        : "Add changes/<version>.md first.",
           data: { expected: ["existing changelog version"], actual: [version] },
         });
}

function emptyRange(from: string, to: string): CliError {
  return new CliError(`No released changelog entries found after ${from} through ${to}.`, {
           exitCode: EXIT_INPUT,
           errorCode: "CHANGELOG_RANGE_EMPTY",
           hint: "Choose a wider range or run `scriptctl changelog <version>` for a known release.",
           data: { expected: ["a version range containing released changelog entries"], actual: [`--from ${from}`, `--to ${to}`] },
         });
}

function bodyForVersions(versions: string[]): string {
  return versions.map(readChangeFile).join("\n\n");
}

export function commandChangelog(opts: ChangelogOptions): [CommandOutput, number] {
  const args = opts._args ?? [];
  const versionArg = args[0]?.trim() ?? "";
  const from = String(opts.from ?? "").trim();
  const to = String(opts.to ?? "").trim();
  const unreleased = Boolean(opts.unreleased);

  if (args.length > 1) {
    throw new CliError("changelog accepts at most one version argument.", {
            exitCode: EXIT_USAGE,
            errorCode: "CHANGELOG_TOO_MANY_ARGS",
            hint: "Pass a single version, or use --from/--to for a range.",
            data: { expected: ["scriptctl changelog [version] [--from X] [--to Y] [--unreleased]"], actual: [args.join(" ")] },
          });
  }
  if (unreleased && (versionArg || from || to)) {
    throw new CliError("--unreleased cannot combine with version, --from, or --to.", {
            exitCode: EXIT_USAGE,
            errorCode: "CHANGELOG_SELECTOR_CONFLICT",
            hint: "Run `scriptctl changelog --unreleased` by itself.",
            data: { expected: ["either --unreleased, a version, or a --from/--to range"], actual: ["--unreleased with another selector"] },
          });
  }
  if (versionArg && (from || to)) {
    throw new CliError("A version argument cannot combine with --from or --to.", {
            exitCode: EXIT_USAGE,
            errorCode: "CHANGELOG_SELECTOR_CONFLICT",
            hint: "Run `scriptctl changelog 0.25.10` or `scriptctl changelog --from 0.25.8 --to 0.25.10`.",
            data: { expected: ["either a version argument, or --from/--to"], actual: [versionArg, from ? `--from ${from}` : "", to ? `--to ${to}` : ""].filter(Boolean) },
          });
  }

  if (unreleased) {
    const body = readChangeFile(UNRELEASED);
    return [{ text: body, data: { content: body, versions: [], unreleased: true } }, EXIT_OK];
  }

  const available = listReleasedVersions();
  const current = readPackageVersion();

  if (versionArg) {
    versionParts(versionArg);
    if (!available.includes(versionArg)) throw missingVersion(versionArg, available);
    const body = readChangeFile(versionArg);
    return [{ text: body, data: { content: body, versions: [versionArg], unreleased: false } }, EXIT_OK];
  }

  if (from || to) {
    if (!from) {
      throw new CliError("--from is required for range queries.", {
              exitCode: EXIT_USAGE,
              errorCode: "CHANGELOG_FROM_MISSING",
              hint: "Run `scriptctl changelog --from 0.25.8 --to 0.25.10`.",
              data: { expected: ["--from <x.y.z>"], actual: [to ? `--to ${to}` : "<missing>"] },
            });
    }
    versionParts(from);
    const upper = to || current;
    versionParts(upper);
    if (!available.includes(upper)) throw missingVersion(upper, available);
    const versions = available.filter((version) => compareVersions(version, from) > 0 && compareVersions(version, upper) <= 0);
    if (versions.length === 0) throw emptyRange(from, upper);
    const content = bodyForVersions(versions);
    return [{ text: content, data: { content, versions, from, to: upper, unreleased: false } }, EXIT_OK];
  }

  if (!available.includes(current)) throw missingVersion(current, available);
  const body = readChangeFile(current);
  return [{ text: body, data: { content: body, versions: [current], unreleased: false } }, EXIT_OK];
}
